// Rapor Tutarlılık Denetimi — paylaşılan şemalar (spec 2026-10-22 §3–§5).
import { z } from "zod";

export const REPORT_CHECK_STATUSES = ["queued", "running", "done", "failed"] as const;
export const REPORT_CHECK_RESULT_STATES = ["complete", "partial"] as const;
export const FINDING_KINDS = ["numeric", "narrative", "terminology"] as const;
export const FINDING_LEVELS = ["high", "medium", "low"] as const;
export const FINDING_STATUSES = ["open", "resolved", "dismissed"] as const;

export type ReportCheckStatus = (typeof REPORT_CHECK_STATUSES)[number];
export type ReportCheckResultState = (typeof REPORT_CHECK_RESULT_STATES)[number];
export type FindingKind = (typeof FINDING_KINDS)[number];
export type FindingLevel = (typeof FINDING_LEVELS)[number];
export type FindingStatus = (typeof FINDING_STATUSES)[number];

/** Koşulan metnin kendisi (değiştirilemez kanıt) — `report_checks.input_sections`. */
export const InputSectionSchema = z.object({
  code: z.string(),
  label: z.string(),
  contentHash: z.string().length(64),
  content: z.string(),
});
export type InputSection = z.infer<typeof InputSectionSchema>;

// --- LLM çıktısı ---
export const ReportCheckLlmFindingSchema = z.object({
  kind: z.enum(FINDING_KINDS),
  severity: z.enum(FINDING_LEVELS),
  confidence: z.enum(FINDING_LEVELS),
  sectionCodes: z.array(z.string()),
  quotes: z.array(z.object({ sectionCode: z.string(), excerpt: z.string() })),
  title: z.string(),
  explanation: z.string(),
  suggestion: z.string(),
});
export const ReportCheckLlmOutputSchema = z.object({
  findings: z.array(ReportCheckLlmFindingSchema),
});
export type ReportCheckLlmOutput = z.infer<typeof ReportCheckLlmOutputSchema>;

/** OpenAI strict json_schema: her nesne kapalı, `required` tüm alanları listeler. */
export const REPORT_CHECK_RESPONSE_JSON_SCHEMA = {
  type: "object",
  additionalProperties: false,
  required: ["findings"],
  properties: {
    findings: {
      type: "array",
      items: {
        type: "object",
        additionalProperties: false,
        required: [
          "kind",
          "severity",
          "confidence",
          "sectionCodes",
          "quotes",
          "title",
          "explanation",
          "suggestion",
        ],
        properties: {
          kind: { type: "string", enum: [...FINDING_KINDS] },
          severity: { type: "string", enum: [...FINDING_LEVELS] },
          confidence: { type: "string", enum: [...FINDING_LEVELS] },
          sectionCodes: { type: "array", items: { type: "string" } },
          quotes: {
            type: "array",
            items: {
              type: "object",
              additionalProperties: false,
              required: ["sectionCode", "excerpt"],
              properties: {
                sectionCode: { type: "string" },
                excerpt: { type: "string" },
              },
            },
          },
          title: { type: "string" },
          explanation: { type: "string" },
          suggestion: { type: "string" },
        },
      },
    },
  },
} as const;

// --- İstekler ---
export const FindingStatusPatchSchema = z.object({
  status: z.enum(FINDING_STATUSES),
  note: z.string().max(2000).optional(),
});
export type FindingStatusPatch = z.infer<typeof FindingStatusPatchSchema>;

export const StartReportCheckSchema = z.object({
  inputFingerprint: z.string().length(64),
});
export type StartReportCheck = z.infer<typeof StartReportCheckSchema>;

// --- DTO'lar ---
export const ReportCheckValidationSchema = z.object({
  droppedUnknownSection: z.number().int().optional(),
  truncatedAt: z.number().int().nullable().optional(),
  outputTruncated: z.boolean().optional(),
  quoteUnverified: z.number().int().optional(),
});
export type ReportCheckValidation = z.infer<typeof ReportCheckValidationSchema>;

export interface ReportCheckEstimate {
  sectionCount: number;
  promptTokens: number;
  outputTokens: number;
  estimatedCostUsd: number;
  model: string;
  provider: string;
  calibrated: boolean;
  inputFingerprint: string;
  limit: { maxInputTokens: number; fits: boolean };
}

export interface ReportCheckSummary {
  id: string;
  projectId: string;
  version: number;
  status: ReportCheckStatus;
  resultState: ReportCheckResultState | null;
  findingCount: number;
  sectionCount: number | null;
  costUsd: number | null;
  estimatedCostUsd: number | null;
  model: string | null;
  failureReason: string | null;
  failureMessage: string | null;
  supersededBy: string | null;
  requestedBy: string | null;
  createdAt: string;
  startedAt: string | null;
  finishedAt: string | null;
}

export interface ReportCheckQuoteDto {
  sectionCode: string;
  excerpt: string;
  verified: boolean;
}

export interface ReportCheckFindingDto {
  id: string;
  checkId: string;
  ordinal: number;
  kind: FindingKind;
  severity: FindingLevel;
  confidence: FindingLevel;
  sectionCodes: string[];
  quotes: ReportCheckQuoteDto[];
  evidenceComplete: boolean;
  title: string;
  explanation: string;
  suggestion: string;
  status: FindingStatus;
  statusNote: string | null;
  statusChangedBy: string | null;
  statusChangedAt: string | null;
  stale: boolean;
  createdAt: string;
}

export interface ReportCheckStale {
  changed: string[];
  added: string[];
  removed: string[];
}

export interface ReportCheckDetail extends ReportCheckSummary {
  validation: ReportCheckValidation | null;
  findings: ReportCheckFindingDto[];
  stale: ReportCheckStale;
  openCountBySection: Record<string, number>;
}
